import { useCallback, useEffect, useState } from 'react'
import { invitesApi, readMyInvitations } from './api'
import type { MyInvitation } from './types'

/**
 * The signed-in account's waiting manager invitations (`GET /api/v1/invitations/mine`, docs/features/event-managers.md),
 * loaded once when `enabled`. They're also emailed, so a failed load shows none rather than an error. `drop` takes one
 * off the list once it is accepted or gone.
 */
export function useMyInvitations(enabled = true) {
  const [invitations, setInvitations] = useState<MyInvitation[]>([])
  const [loading, setLoading] = useState(enabled)

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    invitesApi
      .mine()
      .then((body) => {
        if (!cancelled) setInvitations(readMyInvitations(body) ?? [])
      })
      .catch(() => {
        if (!cancelled) setInvitations([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [enabled])

  const drop = useCallback((invitationId: string) => {
    setInvitations((current) => current.filter((invitation) => invitation.id !== invitationId))
  }, [])

  return { invitations, loading, drop }
}
